import React, { useEffect, useMemo, useRef } from "react";
import { sanitizeRichHtml, stripHtmlToText } from "@/lib/richText";

type ColorKey =
  | "default"
  | "chakra-red"
  | "chakra-orange"
  | "chakra-yellow"
  | "chakra-green"
  | "chakra-blue"
  | "chakra-indigo"
  | "chakra-violet";

export interface RichTextEditorProps {
  valueHtml: string;
  onChange: (nextHtml: string, nextPlainText: string) => void;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  minHeightClassName?: string;
  submitOnEnter?: boolean;
  onSubmit?: () => void;
}

function parseHslTriplet(v: string): { h: string; s: string; l: string } | null {
  // Expected shadcn style: "222.2 84% 4.9%" (space-separated)
  const parts = String(v || "")
    .trim()
    .replace(/;/g, "")
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length < 3) return null;
  return { h: parts[0], s: parts[1], l: parts[2] };
}

function resolveThemeColor(key: ColorKey): string {
  const map: Record<ColorKey, string> = {
    default: "--foreground",
    "chakra-red": "--chakra-red",
    "chakra-orange": "--chakra-orange",
    "chakra-yellow": "--chakra-yellow",
    "chakra-green": "--chakra-green",
    "chakra-blue": "--chakra-blue",
    "chakra-indigo": "--chakra-indigo",
    "chakra-violet": "--chakra-violet",
  };

  try {
    const cssVar = map[key];
    const raw = getComputedStyle(document.documentElement).getPropertyValue(cssVar);
    const triplet = parseHslTriplet(raw);
    if (!triplet) return "#000000";
    return `hsl(${triplet.h}, ${triplet.s}, ${triplet.l})`;
  } catch {
    return "#000000";
  }
}

type Swatch = {
  key: ColorKey;
  cssVar: string;
  label: string;
};

const SWATCHES: Swatch[] = [
  { key: "default", cssVar: "--foreground", label: "Black" },
  { key: "chakra-red", cssVar: "--chakra-red", label: "Red" },
  { key: "chakra-orange", cssVar: "--chakra-orange", label: "Orange" },
  { key: "chakra-yellow", cssVar: "--chakra-yellow", label: "Yellow" },
  { key: "chakra-green", cssVar: "--chakra-green", label: "Green" },
  { key: "chakra-blue", cssVar: "--chakra-blue", label: "Blue" },
  { key: "chakra-indigo", cssVar: "--chakra-indigo", label: "Indigo" },
  { key: "chakra-violet", cssVar: "--chakra-violet", label: "Violet" },
];

export function RichTextEditor({
  valueHtml,
  onChange,
  placeholder,
  disabled,
  maxLength,
  minHeightClassName = "min-h-[260px]",
  submitOnEnter,
  onSubmit,
}: RichTextEditorProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  const sanitizedValue = useMemo(() => sanitizeRichHtml(valueHtml || ""), [valueHtml]);
  const plainText = useMemo(() => stripHtmlToText(sanitizedValue), [sanitizedValue]);

  // Keep DOM in sync with external value
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const current = el.innerHTML;
    if (current !== sanitizedValue) {
      el.innerHTML = sanitizedValue;
    }
  }, [sanitizedValue]);

  const emitChange = () => {
    const el = rootRef.current;
    if (!el) return;
    const nextHtml = sanitizeRichHtml(el.innerHTML);
    const nextPlain = stripHtmlToText(nextHtml);
    onChange(nextHtml, nextPlain);
  };

  const canInsertMore = (incomingPlainTextLen: number) => {
    if (maxLength == null) return true;
    const currentPlain = stripHtmlToText(sanitizeRichHtml(rootRef.current?.innerHTML || ""));
    return currentPlain.length + incomingPlainTextLen <= maxLength;
  };

  const handleBeforeInput = (e: React.FormEvent<HTMLDivElement> & { nativeEvent: InputEvent }) => {
    if (disabled) return;
    if (maxLength == null) return;

    const inputType = e.nativeEvent?.inputType;
    if (!inputType) return;

    // Allow deletions
    if (inputType.startsWith("delete")) return;

    // Approximate: if inserting plain text, block if it would exceed.
    // For rich insertions (formatting), let it pass.
    if (inputType === "insertText") {
      const data = (e.nativeEvent as any).data as string | null;
      const incoming = String(data ?? "");
      if (incoming && !canInsertMore(incoming.length)) {
        e.preventDefault();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    if (disabled) return;

    e.preventDefault();
    const text = e.clipboardData.getData("text/plain") || "";
    let next = text;
    if (maxLength != null) {
      const currentPlain = stripHtmlToText(sanitizeRichHtml(rootRef.current?.innerHTML || ""));
      const remaining = Math.max(0, maxLength - currentPlain.length);
      next = next.slice(0, remaining);
    }

    if (next) {
      document.execCommand("insertText", false, next);
      emitChange();
    }
  };

  const applyCommand = (cmd: "bold" | "italic") => {
    if (disabled) return;
    rootRef.current?.focus();
    document.execCommand(cmd);
    emitChange();
  };

  const applyColor = (nextKey: ColorKey) => {
    if (disabled) return;

    const el = rootRef.current;
    if (!el) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    // Only apply color when the user has selected some text inside this editor.
    if (selection.isCollapsed) return;
    if (!el.contains(range.commonAncestorContainer)) return;

    // Apply color to the selected text, then reset typing color back to default.
    el.focus();
    const cssColor = resolveThemeColor(nextKey);
    const defaultTypingColor = getComputedStyle(el).color;

    document.execCommand("styleWithCSS", false, "true");
    document.execCommand("foreColor", false, cssColor);
    // Collapse selection to the end, then reset the insertion color.
    try {
      window.getSelection()?.collapseToEnd();
    } catch {
      // ignore
    }
    document.execCommand("foreColor", false, defaultTypingColor);
    emitChange();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (submitOnEnter && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit?.();
      return;
    }
  };

  const showPlaceholder = !plainText.trim();

  return (
    <div className="w-full max-w-full overflow-hidden rounded-xl border bg-background/80">
      <div className="flex flex-wrap items-center gap-2 border-b px-2 py-2">
        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted disabled:opacity-50"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => applyCommand("bold")}
          disabled={disabled}
          aria-label="Bold"
          title="Bold"
        >
          <span className="text-xs font-bold">B</span>
        </button>
        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted disabled:opacity-50"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => applyCommand("italic")}
          disabled={disabled}
          aria-label="Italic"
          title="Italic"
        >
          <span className="text-xs italic">I</span>
        </button>

        <div className="ml-1 flex min-w-0 items-center gap-2">
          <div
            className="flex flex-wrap items-center gap-2"
            aria-label="Text color"
            title="Text color"
          >
            {SWATCHES.map((s) => {
              return (
                <button
                  key={s.key}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applyColor(s.key)}
                  disabled={disabled}
                  aria-label={s.label}
                  title={s.label}
                  className={[
                    "h-5 w-5 rounded-full border",
                    disabled ? "opacity-50" : "hover:opacity-90",
                  ].join(" ")}
                  style={{ backgroundColor: `hsl(var(${s.cssVar}))`, borderColor: `hsl(var(--border))` }}
                />
              );
            })}
          </div>
        </div>
      </div>

      <div className="relative">
        {showPlaceholder && (
          <div className="pointer-events-none absolute left-3 top-3 text-sm text-muted-foreground">
            {placeholder}
          </div>
        )}
        <div
          ref={rootRef}
          className={[
            "max-w-full break-words px-3 py-2 text-sm leading-relaxed outline-none",
            minHeightClassName,
            disabled ? "bg-muted/30 text-muted-foreground" : "",
          ].join(" ")}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onBlur={() => emitChange()}
          onInput={() => emitChange()}
          onBeforeInput={handleBeforeInput as any}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
        />
      </div>
    </div>
  );
}
